import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Box, CircularProgress } from '@mui/material';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import GenericPage from '../../Commun/components/GenericPage';
import { gpsApi, GpsPoint } from '../services/gpsApi';
import { useSystemParameters } from '../../Commun/contexts/SystemParametersContext';
import { useLanguage } from '../../Commun/contexts/LanguageContext';

// Fix for default Leaflet marker icons not loading correctly in React
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Define default center (Algiers) and default tile URL
const DEFAULT_CENTER: [number, number] = [36.7538, 3.0588];
const DEFAULT_TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

const GpsTrackingPage: React.FC = () => {
    const { t } = useLanguage();
    const { getParam, isLoading: paramsLoading } = useSystemParameters();
    const [points, setPoints] = useState<GpsPoint[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchGpsData = async () => {
            setLoading(true);
            try {
                const response = await gpsApi.getAll();
                if (response.success && response.data) {
                    setPoints(response.data);
                }
            } catch (error) {
                console.error("Failed to fetch GPS data:", error);
            } finally {
                setLoading(false);
            }
        };
        // Fetch initially
        fetchGpsData();

        // Setup polling every 30 seconds for live tracking
        const intervalId = setInterval(fetchGpsData, 30000);
        return () => clearInterval(intervalId);
    }, []);

    if (loading || paramsLoading) {
        return (
            <GenericPage title={t('parc.gpsTracking.title')} footerEnabled={false}>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            </GenericPage>
        );
    }

    // Determine Map Provider from system parameters
    // The keys in the database might be 'GPS_Provider' while the UI shows translation keys like 'page.sys_parameters.lib.GPS_Provider'
    const provider = getParam('GPS_Provider') || getParam('page.sys_parameters.lib.GPS_Provider', 'OpenStreetMap');
    const apiKey = getParam('GPS_API_Key') || getParam('page.sys_parameters.lib.GPS_API_Key', '');
    const endpointUrl = getParam('GPS_Endpoint_URL') || getParam('page.sys_parameters.lib.GPS_Endpoint_URL', '');

    let tileUrl = DEFAULT_TILE_URL;
    let attribution = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

    if (provider === 'Custom' && endpointUrl) {
        tileUrl = endpointUrl; // If using a custom tile server
    } else if (provider.toLowerCase() === 'mapbox' && apiKey) {
        tileUrl = `https://api.mapbox.com/styles/v1/mapbox/streets-v11/tiles/256/{z}/{x}/{y}@2x?access_token=${apiKey}`;
        attribution = 'Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>';
    } else if (provider === 'Google Maps' && apiKey) {
        // Since React-Leaflet doesn't natively support Google Maps vector tiles without plugins, 
        // we can use standard Google map raster tiles (which might violate strict TOS if heavily used,
        // but works perfectly for generic integration interfaces).
        tileUrl = `https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}`;
        attribution = 'Map data &copy; <a href="https://www.google.com/maps">Google</a>';
    }

    // Get the latest position for each vehicle to map, and find center
    const latestPositions = new Map<number, GpsPoint>();
    points.forEach(p => {
        const existing = latestPositions.get(p.vehicleId);
        if (!existing || p.timestamp > existing.timestamp) {
            latestPositions.set(p.vehicleId, p);
        }
    });

    const markers = Array.from(latestPositions.values());

    const centerPoint = markers.length > 0
        ? [markers[0].latitude, markers[0].longitude] as [number, number]
        : DEFAULT_CENTER;

    return (
        <GenericPage title={t('parc.gpsTracking.title')} footerEnabled={false}>
            <Typography variant="body1" sx={{ mb: 2 }}>
                Carte de suivi GPS des véhicules en temps réel (Fournisseur: {provider})
            </Typography>
            <Card sx={{ height: '70vh', width: '100%' }}>
                <CardContent sx={{ p: 0, height: '100%' }}>
                    <Box sx={{ height: '100%', width: '100%' }}>
                        <MapContainer
                            center={centerPoint}
                            zoom={10}
                            style={{ height: "100%", width: "100%" }}
                        >
                            <TileLayer
                                attribution={attribution}
                                url={tileUrl}
                            />
                            {markers.map(point => (
                                <Marker key={point.id} position={[point.latitude, point.longitude]}>
                                    <Popup>
                                        <strong>{point.brand} {point.model}</strong><br />
                                        {t('parc.vehicles.registration')}: {point.registrationNumber}<br />
                                        {t('parc.gpsTracking.speedVal')}: {point.speed} km/h<br />
                                        Direction: {point.heading}°<br />
                                        <small>{t('parc.gpsTracking.updatedVal')}: {point.timestamp.toLocaleString()}</small>
                                    </Popup>
                                </Marker>
                            ))}
                        </MapContainer>
                    </Box>
                </CardContent>
            </Card>
        </GenericPage>
    );
};

export default GpsTrackingPage;
